import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import {
  Save, Star, Phone, Globe, MapPin, Users, Mail, ExternalLink,
  MessageCircle, ShieldCheck, ShieldX, Loader2, Lock, Search,
  CreditCard, Inbox, ChevronDown, Check, Clock, Calendar,
  ChevronRight, Heart, Image as ImageIcon,
} from "lucide-react";
import { Button, Card, Badge } from "@/components/kit";
import Pagination from "@/components/Pagination";
import ScrapingLoader from "@/components/ScrapingLoader";
import api, { apiErr } from "@/lib/api";

const fmtNum = (n) => Number(n || 0).toLocaleString("id-ID");
const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const normWeb = (w) => (!w ? "" : w.startsWith("http") ? w : `https://${w}`);

const profileUrl = (r, source) => {
  const h = (r.handle || "").replace(/^@/, "");
  if (source === "facebook") return h ? `https://www.facebook.com/${h}` : (r.profile_url || "").replace("web.facebook.com", "www.facebook.com").replace(/[?#].*$/, "");
  if (source === "instagram") return h ? `https://www.instagram.com/${h}` : r.profile_url;
  return r.profile_url;
};

const mapsUrl = (r) => {
  if (r.lat && r.lng) return `https://www.google.com/maps/search/?api=1&query=${r.lat},${r.lng}`;
  const q = encodeURIComponent([r.business_name, r.address].filter(Boolean).join(" "));
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
};

const initials = (name) => {
  if (!name) return "?";
  return name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
};

const SOURCE_COLORS = {
  google_maps: { bg: "bg-sky-50", text: "text-sky-600", ring: "ring-sky-200", badge: "bg-sky-500" },
  facebook: { bg: "bg-blue-50", text: "text-[#1877F2]", ring: "ring-blue-200", badge: "bg-[#1877F2]" },
  instagram: { bg: "bg-pink-50", text: "text-pink-600", ring: "ring-pink-200", badge: "bg-gradient-to-br from-amber-500 via-pink-500 to-purple-500" },
};

// ─── Rich Card for Social Sources ───
function SocialCard({ r, source, selected, toggle, colors, isSaved, waStatus, onCheckWa, canValidate, waPending }) {
  const [expanded, setExpanded] = useState(false);
  const h = (r.handle || "").replace(/^@/, "");
  const profileLink = h ? (source === "facebook" ? `https://www.facebook.com/${h}` : `https://www.instagram.com/${h}`) : (r.profile_url || "");

  return (
    <div className={`rounded-2xl border overflow-hidden transition-all duration-200 group
      ${selected ? `${colors.bg} border-current ${colors.ring} ring-2` : "border-slate-100 hover:border-slate-200 bg-white"}
      ${isSaved ? "opacity-50" : ""}`}
      style={{ animationDelay: "0ms" }}>

      {/* Cover photo */}
      {r.cover_photo && (
        <div className="relative h-28 overflow-hidden bg-slate-100">
          <img src={r.cover_photo} alt="" className="w-full h-full object-cover" loading="lazy"
            onError={(e) => { e.target.style.display = "none"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          {r.followers > 0 && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full">
              <Users className="w-3 h-3" /> {fmtNum(r.followers)}
            </div>
          )}
          {r.ad_status?.includes("running ads") && (
            <div className="absolute top-2 right-2 bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
              <Heart className="w-2.5 h-2.5" /> Running Ads
            </div>
          )}
        </div>
      )}

      <div className="p-4">
        {/* Header: avatar + name */}
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <input type="checkbox" checked={!!selected} disabled={isSaved}
            onChange={() => toggle(r.external_id)}
            className="w-4 h-4 rounded cursor-pointer mt-1 shrink-0" />

          {/* Profile photo */}
          <div className="relative shrink-0">
            {r.profile_photo ? (
              <img src={r.profile_photo} alt="" className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-sm" loading="lazy"
                onError={(e) => { e.target.outerHTML = `<div class="w-12 h-12 rounded-xl ${colors.bg} grid place-items-center border-2 border-white shadow-sm"><span class="text-sm font-bold ${colors.text}">${initials(r.business_name)}</span></div>`; }} />
            ) : (
              <div className={`w-12 h-12 rounded-xl ${colors.bg} grid place-items-center border-2 border-white shadow-sm`}>
                <span className={`text-sm font-bold ${colors.text}`}>{initials(r.business_name)}</span>
              </div>
            )}
          </div>

          {/* Name + category */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display font-bold text-sm text-slate-800 leading-tight truncate">{r.business_name}</h3>
              {isSaved && <span className="text-[9px] font-bold text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded-full">Tersimpan</span>}
            </div>
            {r.category && (
              <span className="inline-block bg-sky-50 text-sky-600 text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1">{r.category}</span>
            )}
            {r.intro && (
              <p className="text-[11px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">{r.intro}</p>
            )}
          </div>
        </div>

        {/* Info chips row */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {r.phone && (
            <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 text-[11px] bg-sky-50 text-sky-600 px-2.5 py-1 rounded-lg hover:bg-sky-100 transition font-medium">
              <Phone className="w-3 h-3" /> {r.phone}
            </a>
          )}
          {r.email && (
            <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-[11px] bg-violet-50 text-violet-600 px-2.5 py-1 rounded-lg hover:bg-violet-100 transition font-medium truncate max-w-[180px]" title={r.email}>
              <Mail className="w-3 h-3" /> <span className="truncate">{r.email}</span>
            </a>
          )}
          {r.website && (
            <a href={normWeb(r.website)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-600 px-2.5 py-1 rounded-lg hover:bg-emerald-100 transition font-medium truncate max-w-[180px]" title={r.website}>
              <Globe className="w-3 h-3" /> <span className="truncate">{new URL(normWeb(r.website)).hostname}</span>
            </a>
          )}
          {r.address && (
            <a href={mapsUrl(r)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] bg-amber-50 text-amber-600 px-2.5 py-1 rounded-lg hover:bg-amber-100 transition font-medium">
              <MapPin className="w-3 h-3" /> Lokasi
            </a>
          )}
          {profileLink && (
            <a href={profileLink} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg transition font-medium ${source === "facebook" ? "bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20" : "bg-pink-50 text-pink-600 hover:bg-pink-100"}`}>
              {source === "facebook" ? "Facebook" : "Instagram"} <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>

        {/* Expandable details */}
        {(r.rating_text || r.business_hours || r.creation_date || r.price_range || (r.websites && r.websites.length > 1) || r.address) && (
          <>
            <button onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-sky-500 mt-2 font-semibold transition">
              {expanded ? "Sembunyikan" : "Detail lainnya"}
              <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
            </button>
            {expanded && (
              <div className="mt-2 p-3 bg-slate-50 rounded-xl space-y-1.5 text-[11px]">
                {r.address && (
                  <div className="flex gap-2"><span className="text-slate-400 shrink-0">📍 Alamat:</span><span className="text-slate-600">{r.address}</span></div>
                )}
                {r.rating_text && (
                  <div className="flex gap-2"><span className="text-slate-400 shrink-0">⭐ Rating:</span><span className="text-slate-600">{r.rating_text}</span></div>
                )}
                {r.business_hours && (
                  <div className="flex gap-2"><span className="text-slate-400 shrink-0"><Clock className="w-3 h-3 inline" /> Jam:</span><span className="text-slate-600">{r.business_hours}</span></div>
                )}
                {r.price_range && (
                  <div className="flex gap-2"><span className="text-slate-400 shrink-0">💰 Harga:</span><span className="text-slate-600">{r.price_range}</span></div>
                )}
                {r.creation_date && (
                  <div className="flex gap-2"><span className="text-slate-400 shrink-0"><Calendar className="w-3 h-3 inline" /> Dibuat:</span><span className="text-slate-600">{r.creation_date}</span></div>
                )}
                {r.websites && r.websites.length > 1 && (
                  <div>
                    <span className="text-slate-400">🔗 Links lainnya:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {r.websites.slice(1).map((w, i) => (
                        <a key={i} href={normWeb(w)} target="_blank" rel="noreferrer"
                          className="text-[10px] text-sky-500 hover:underline truncate max-w-[200px] block">{new URL(normWeb(w)).hostname}</a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* WA check button */}
        <div className="flex items-center justify-end mt-2 pt-2 border-t border-slate-50">
          {!r.phone ? (
            <span className="text-[10px] text-slate-300">Tanpa nomor</span>
          ) : waPending[r.external_id] ? (
            <Loader2 className="w-4 h-4 text-sky-500 animate-spin" />
          ) : r.wa_ready === true ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
              <ShieldCheck className="w-3 h-3" /> WA Aktif
            </span>
          ) : r.wa_ready === false ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-500 bg-red-50 px-2.5 py-1 rounded-full">
              <ShieldX className="w-3 h-3" /> Tidak ada WA
            </span>
          ) : !canValidate ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-slate-400"><Lock className="w-3 h-3" /> Premium</span>
          ) : (
            <button onClick={() => onCheckWa([r])} disabled={false}
              className="text-[10px] font-bold text-emerald-600 hover:bg-emerald-50 rounded-lg px-2.5 py-1 inline-flex items-center gap-1 transition">
              <MessageCircle className="w-3 h-3" /> Cek WA
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───
export default function ScrapeResults({ source, results, setResults, selected, toggle, selectedLeads, save, saving, emptyHint, loading, progress, suggestions }) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [waChecking, setWaChecking] = useState(false);
  const [waPending, setWaPending] = useState({});
  const [waAccount, setWaAccount] = useState(null);
  const isSocial = source === "facebook" || source === "instagram";
  const colors = SOURCE_COLORS[source] || SOURCE_COLORS.google_maps;

  useEffect(() => {
    api.get("/wa/account").then((r) => setWaAccount(r.data)).catch(() => setWaAccount({ connected: false }));
  }, []);

  const canValidate = !!(waAccount?.connected && waAccount?.device?.can_validate);
  const waBlockReason = !waAccount?.connected
    ? "Hubungkan akun Fonnte (WA Devices) dulu untuk Cek WA."
    : !waAccount?.device?.can_validate
      ? `Cek WA butuh paket Fonnte premium. Paket sekarang: ${waAccount?.device?.package || "—"}.`
      : "";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return results;
    return results.filter((r) =>
      [r.business_name, r.phone, r.email, r.address, r.category, r.city, r.intro]
        .some((v) => (v || "").toString().toLowerCase().includes(q))
    );
  }, [results, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  const togglePage = (val) => {
    pageRows.forEach((r) => {
      if (r.already_saved) return;
      const cur = !!selected[r.external_id];
      if (cur !== val) toggle(r.external_id);
    });
  };
  const pageAllSelected = pageRows.length > 0 && pageRows.every((r) => r.already_saved || selected[r.external_id]);
  const selectedCount = selectedLeads.length;

  const checkWa = async (rows) => {
    if (!canValidate) { toast.error(waBlockReason); return; }
    const targets = rows.filter((r) => r.phone && r.wa_ready === undefined);
    if (targets.length === 0) { toast.info("Tidak ada nomor baru untuk dicek"); return; }
    setWaChecking(true);
    const pending = {}; targets.forEach((r) => (pending[r.external_id] = true));
    setWaPending((s) => ({ ...s, ...pending }));
    try {
      const { data } = await api.post("/wa/validate", { numbers: targets.map((r) => r.phone) });
      const map = data.results || {};
      if (setResults) {
        setResults((rs) => rs.map((r) => {
          if (!targets.find((t) => t.external_id === r.external_id)) return r;
          const ready = map[r.phone];
          if (ready === true || ready === false) return { ...r, wa_ready: ready };
          return r;
        }));
      }
      const okCount = Object.values(map).filter((v) => v === true).length;
      const noCount = Object.values(map).filter((v) => v === false).length;
      const unkCount = targets.length - okCount - noCount;
      toast.success(`${okCount} terdaftar · ${noCount} tidak${unkCount ? ` · ${unkCount} tidak diketahui` : ""}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setWaPending({}); setWaChecking(false); }
  };

  if (loading && results.length === 0) return <ScrapingLoader source={source} progress={progress} />;

  if (results.length === 0) {
    return (
      <Card className="p-12 text-center" data-testid="scrape-empty">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-50 grid place-items-center mb-4">
          <Inbox className="w-8 h-8 text-slate-300" />
        </div>
        <p className="text-sm font-semibold text-slate-500 mb-1">{emptyHint || "Belum ada hasil pencarian"}</p>
        <p className="text-xs text-slate-400 mb-4">Masukkan keyword dan klik Cari untuk memulai.</p>

        {suggestions && (suggestions.keywords?.length > 0 || suggestions.cities?.length > 0) && (
          <div className="mt-4 p-4 bg-sky-50/50 rounded-xl border border-sky-100 text-left max-w-sm mx-auto">
            <p className="text-[11px] font-bold text-sky-600 uppercase tracking-wider mb-2">Saran pencarian</p>
            {suggestions.keywords?.length > 0 && (
              <div className="mb-2">
                <p className="text-[10px] text-slate-400 mb-1">Coba keyword lain:</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.keywords.map((kw) => (
                    <span key={kw} className="text-[11px] bg-white border border-sky-200 text-sky-600 px-2 py-0.5 rounded-full">{kw}</span>
                  ))}
                </div>
              </div>
            )}
            {suggestions.cities?.length > 0 && (
              <div>
                <p className="text-[10px] text-slate-400 mb-1">Atau kota terdekat:</p>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.cities.map((c) => (
                    <span key={c} className="text-[11px] bg-white border border-sky-200 text-sky-600 px-2 py-0.5 rounded-full capitalize">{c}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-3 animate-float-up" data-testid="scrape-results-table">

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 glass rounded-2xl px-5 py-3.5 border border-slate-100">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${colors.bg} grid place-items-center`}>
              <span className={`text-xs font-bold ${colors.text}`}>{selectedCount}</span>
            </div>
            <span className="text-xs text-slate-500">
              dipilih<br />
              <span className="text-slate-400">dari {filtered.length} hasil</span>
            </span>
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="scrape-search"
              type="text"
              placeholder="Cari di hasil..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="rounded-xl border border-slate-200 pl-8 pr-3 py-2 text-xs w-44 focus:border-sky-300 focus:ring-2 focus:ring-sky-100 outline-none transition"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="scrape-check-wa-bulk" variant="outline" size="sm"
            onClick={() => checkWa(selectedCount > 0 ? selectedLeads : pageRows)}
            loading={waChecking} disabled={!canValidate}
            title={canValidate ? "Cek apakah nomor terdaftar di WhatsApp" : waBlockReason}
            className="rounded-xl text-xs">
            {canValidate ? <MessageCircle className="w-3.5 h-3.5 text-emerald-500" /> : <Lock className="w-3.5 h-3.5 text-slate-400" />}
            Cek WA {selectedCount > 0 ? `(${selectedCount})` : "(halaman)"}
          </Button>
          <Button data-testid="scrape-save" onClick={save} loading={saving} size="sm" className="rounded-xl text-xs">
            <Save className="w-3.5 h-3.5" /> Simpan Lead
          </Button>
        </div>
      </div>

      {/* Social: Card grid | Maps: Table */}
      {isSocial ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {pageRows.map((r) => (
            <SocialCard
              key={r.external_id}
              r={r}
              source={source}
              selected={!!selected[r.external_id]}
              toggle={toggle}
              colors={colors}
              isSaved={r.already_saved}
              waPending={waPending}
              canValidate={canValidate}
            />
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50/80 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  <th className="p-3 w-10">
                    <input type="checkbox" data-testid="scrape-select-page"
                      checked={pageAllSelected} onChange={(e) => togglePage(e.target.checked)}
                      className="w-4 h-4 rounded cursor-pointer" />
                  </th>
                  <th className="p-3 text-left">Bisnis</th>
                  <th className="p-3 text-left hidden md:table-cell">Kontak</th>
                  <th className="p-3 text-left hidden lg:table-cell">Alamat</th>
                  <th className="p-3 text-left">Rating</th>
                  <th className="p-3 text-center">WhatsApp</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, idx) => (
                  <tr key={r.external_id} data-testid="scrape-result-row"
                    className={`border-t border-slate-50 hover:bg-slate-50/50 transition-all duration-150 group
                      ${r.already_saved ? "opacity-50" : ""} ${selected[r.external_id] ? `${colors.bg}/30` : ""}`}
                    style={{ animationDelay: `${idx * 30}ms` }}>
                    <td className="p-3 pl-4">
                      <input type="checkbox" checked={!!selected[r.external_id]} disabled={r.already_saved}
                        onChange={() => toggle(r.external_id)}
                        className="w-4 h-4 rounded cursor-pointer disabled:cursor-not-allowed" />
                    </td>
                    <td className="p-3 align-top">
                      <div className="flex items-start gap-2.5">
                        <div className={`w-8 h-8 rounded-lg ${colors.bg} grid place-items-center shrink-0 mt-0.5`}>
                          <span className={`text-[10px] font-bold ${colors.text}`}>{initials(r.business_name)}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-semibold text-slate-800 text-[13px] leading-tight">{r.business_name}</span>
                            {r.already_saved && <span className="text-[9px] font-bold text-emerald-500 bg-emerald-50 px-1.5 py-0.5 rounded-full">Tersimpan</span>}
                          </div>
                          <div className="flex items-center gap-2.5 mt-0.5">
                            <a href={mapsUrl(r)} target="_blank" rel="noreferrer" title="Lihat di Google Maps"
                              data-testid="result-maps-link"
                              className="text-[11px] text-sky-500 hover:text-sky-600 hover:underline inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> Lokasi
                            </a>
                            {r.website && (
                              <a href={normWeb(r.website)} target="_blank" rel="noreferrer" title="Website"
                                data-testid="result-website-link"
                                className="text-[11px] text-sky-500 hover:text-sky-600 hover:underline inline-flex items-center gap-1">
                                <Globe className="w-3 h-3" /> Web
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell align-top">
                      <div className="flex flex-col gap-1 text-xs text-slate-600">
                        {r.phone && (
                          <span className="flex items-center gap-1.5">
                            <span className="w-5 h-5 rounded bg-sky-50 grid place-items-center"><Phone className="w-3 h-3 text-sky-500" /></span>
                            {r.phone}
                          </span>
                        )}
                        {r.email && (
                          <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-sky-600 truncate max-w-[200px]" title={r.email}>
                            <span className="w-5 h-5 rounded bg-violet-50 grid place-items-center"><Mail className="w-3 h-3 text-violet-500" /></span>
                            <span className="truncate">{r.email}</span>
                          </a>
                        )}
                        {!r.phone && !r.email && <span className="text-slate-300 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="p-3 hidden lg:table-cell text-xs text-slate-500 align-top">
                      <span className="line-clamp-2 max-w-[240px] text-[12px] leading-relaxed">{r.address || "—"}</span>
                    </td>
                    <td className="p-3 align-top">
                      {r.rating > 0
                        ? <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-600 text-[11px] font-semibold px-2 py-0.5 rounded-full"><Star className="w-3 h-3" fill="currentColor" /> {r.rating}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="p-3 text-center align-top">
                      {!r.phone ? (
                        <span className="text-slate-300 text-[11px]">—</span>
                      ) : waPending[r.external_id] ? (
                        <Loader2 className="w-4 h-4 text-sky-500 animate-spin mx-auto" />
                      ) : r.wa_ready === true ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                          <ShieldCheck className="w-3 h-3" /> WA
                        </span>
                      ) : r.wa_ready === false ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-500 bg-red-50 px-2 py-1 rounded-full">
                          <ShieldX className="w-3 h-3" /> Tidak
                        </span>
                      ) : !canValidate ? (
                        <span title={waBlockReason} className="inline-flex items-center gap-1 text-[10px] text-slate-400 cursor-help">
                          <Lock className="w-3 h-3" /> Premium
                        </span>
                      ) : (
                        <button data-testid="scrape-check-wa-single" onClick={() => checkWa([r])} disabled={waChecking}
                          className="text-[10px] font-bold text-emerald-600 hover:bg-emerald-50 rounded-lg px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50 transition">
                          <MessageCircle className="w-3 h-3" /> Cek
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <div className="flex items-center gap-2 text-[11px] text-slate-400">
          <span>Tampilkan</span>
          <select value={perPage} data-testid="scrape-per-page"
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 focus:border-sky-300 outline-none">
            {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span>· halaman {page}/{totalPages}</span>
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} />
      </div>
    </div>
  );
}
